import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";
import ReactModalAdapter from "helpers/ReactModalAdapter.js";
import { ReactComponent as CheckIcon } from "feather-icons/dist/icons/check.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as CalendarIcon } from "feather-icons/dist/icons/calendar.svg";
import CippeMasterClassImage from "assets/cippmasterclass.jpeg";

const MasterClassModal = styled(ReactModalAdapter)`
  &.masterClassModal__overlay {
    ${tw`fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6`}
    background: rgba(15, 23, 42, 0.78);
    backdrop-filter: blur(6px);
    transform: scale(1);
  }

  &.masterClassModal__content {
    ${tw`relative w-full overflow-y-auto rounded-lg bg-white shadow-2xl outline-none`}
    max-width: 920px;
    max-height: calc(100vh - 2rem);
  }
`;
const ModalLayout = tw.div`grid md:grid-cols-2`;
const ModalVisual = styled.div`
  ${tw`relative flex min-h-48 items-end overflow-hidden p-5 text-white sm:min-h-56 md:min-h-full md:p-8`}
  background: linear-gradient(145deg, #0f766e 0%, #0891b2 58%, #2563eb 100%);

  &:after {
    content: "";
    ${tw`absolute inset-0`}
    background: linear-gradient(to top, rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.08) 70%);
  }
`;
const ModalImage = tw.img`absolute inset-0 h-full w-full object-cover`;
const ModalVisualContent = tw.div`relative z-10`;
const ModalVisualLabel = styled.span`
  ${tw`inline-flex rounded px-3 py-1 text-xs font-bold uppercase text-gray-900`}
  background: #fbbf24;
`;
const ModalVisualText = tw.p`mt-3 max-w-sm text-lg font-bold leading-snug sm:text-xl`;
const ModalContent = tw.div`px-6 py-8 sm:px-8 sm:py-10`;
const CloseModalButton = styled.button`
  ${tw`absolute right-0 top-0 z-20 mt-3 mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition duration-200 focus:outline-none focus:shadow-outline`}

  &:hover,
  &:focus {
    color: #0f766e;
    transform: rotate(6deg);
  }
`;
const ModalEyebrow = styled.p`
  ${tw`pr-12 text-xs font-bold uppercase sm:text-sm`}
  color: #0f766e;
`;
const ModalHeading = tw.h2`mt-2 text-2xl font-black leading-tight text-gray-900 sm:text-3xl`;
const ModalHeadingSubtitle = tw.span`text-lg sm:text-xl`;
const ModalText = tw.p`mt-4 text-sm leading-relaxed text-gray-700 sm:text-base`;
const ModalList = tw.ul`mt-5 space-y-3`;
const ModalListItem = tw.li`flex items-start text-sm leading-relaxed text-gray-700 sm:text-base`;
const ModalCheckIcon = styled(CheckIcon)`
  ${tw`mr-3 mt-1 h-4 w-4 flex-shrink-0`}
  color: #0f766e;
`;
const RegisterButton = styled.a`
  ${tw`mt-8 inline-flex w-full items-center justify-center rounded bg-primary-500 px-8 py-3 font-bold text-white shadow-lg transition duration-300 focus:outline-none focus:shadow-outline sm:w-auto`}

  &:hover,
  &:focus {
    ${tw`bg-primary-900`}
    color: white;
    transform: translateY(-2px);
  }
`;
const MasterClassLauncher = tw.div`fixed right-0 top-1/2 z-40 -translate-y-1/2`;
const MasterClassLauncherButton = tw.button`flex h-12 items-center justify-center gap-1 rounded-l-lg bg-primary-500 px-2 text-white shadow-lg transition duration-200 hocus:bg-primary-700 focus:outline-none focus:shadow-outline`;
const MasterClassLauncherIcon = tw(CalendarIcon)`h-5 w-5 flex-shrink-0`;
const MasterClassLauncherLabel = tw.span`w-20 text-left text-xs font-bold leading-tight`;

export default function CippeMasterClassLauncher() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname !== "/") {
      setIsModalOpen(false);
      return undefined;
    }

    if (navigator.userAgent === "ReactSnap") return undefined;

    const timer = setTimeout(() => setIsModalOpen(true), 1500);
    return () => clearTimeout(timer);
  }, [pathname]);

  return createPortal(
    <>
      {pathname !== "/cippe-master-class-registration" && (
        <MasterClassLauncher>
          <MasterClassLauncherButton
            aria-label="Open CIPP/E Master Class registration"
            onClick={() => setIsModalOpen(true)}
            type="button"
          >
            <MasterClassLauncherIcon />
            <MasterClassLauncherLabel>CIPP/E Master Class</MasterClassLauncherLabel>
          </MasterClassLauncherButton>
        </MasterClassLauncher>
      )}
      <MasterClassModal
        closeTimeoutMS={300}
        className="masterClassModal"
        contentLabel="CIPP/E Master Class registration"
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        shouldCloseOnOverlayClick={true}
      >
        <CloseModalButton
          aria-label="Close CIPP/E Master Class popup"
          type="button"
          onClick={() => setIsModalOpen(false)}
        >
          <CloseIcon tw="h-6 w-6" />
        </CloseModalButton>
        <ModalLayout>
          <ModalVisual>
            <ModalImage
              alt="CIPP/E data protection master class preview"
              src={CippeMasterClassImage}
            />
            <ModalVisualContent>
              <ModalVisualLabel>Live master class</ModalVisualLabel>
              <ModalVisualText>Build a career around the rules shaping global data privacy.</ModalVisualText>
            </ModalVisualContent>
          </ModalVisual>
          <ModalContent>
            <ModalEyebrow />
            <ModalHeading>
              CIPP/E Masterclass
              <br />
              <ModalHeadingSubtitle>
                Master European Data Protection
                <br />
                Prepare with Confidence
              </ModalHeadingSubtitle>
            </ModalHeading>
            <ModalText>
              Learn GDPR. Understand Privacy. Crack <strong>CIPP/E</strong>.
            </ModalText>
            <ModalList>
              <ModalListItem>
                <ModalCheckIcon />
                <span>Exam-Focused Learning</span>
              </ModalListItem>
              <ModalListItem>
                <ModalCheckIcon />
                <span>Practical GDPR Concepts</span>
              </ModalListItem>
              <ModalListItem>
                <ModalCheckIcon />
                <span>Smart Question-Solving Strategies</span>
              </ModalListItem>
              <ModalListItem>
                <ModalCheckIcon />
                <span>Career-Ready Privacy Skills</span>
              </ModalListItem>
            </ModalList>
            <RegisterButton href="/cippe-master-class-registration">Reserve My Seat &rarr;</RegisterButton>
          </ModalContent>
        </ModalLayout>
      </MasterClassModal>
    </>,
    document.body
  );
}